import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  PlusCircle,
  Edit,
  Trash2,
  Search,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Plus,
  Minus,
  Book,
  List,
  Upload,
  Image,
} from "lucide-react";

const AdminShortCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image: "",
    registration_date: "",
    duration: "",
    featured: false,
    modules: [], // Initialize empty modules array
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/short-courses`
      );
      setCourses(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching short courses:", err);
      setError("Failed to load courses. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredCourses = courses.filter((course) =>
    course.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddClick = () => {
    setFormData({
      name: "",
      description: "",
      image: "",
      registration_date: "",
      duration: "",
      featured: false,
      modules: [{ title: "", topics: [""] }], // Initialize with one empty module and topic
    });
    setSelectedImage(null);
    setImagePreview(null);
    setShowAddModal(true);
  };

  const handleEditClick = (course) => {
    setCurrentCourse(course);
    setFormData({
      name: course.name,
      description: course.description,
      image: course.image || "",
      registration_date: course.registration_date || "",
      duration: course.duration || "",
      featured: course.featured || false,
      modules: course.modules || [{ title: "", topics: [""] }], // Use existing modules or initialize
    });
    setSelectedImage(null);
    setImagePreview(course.image || null);
    setShowEditModal(true);
  };

  const handleDeleteClick = (course) => {
    setCurrentCourse(course);
    setShowDeleteModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);

      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      // Clear the image URL field since we're using a file now
      setFormData({
        ...formData,
        image: "",
      });
    }
  };

  // Handle manual image URL input
  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData({
      ...formData,
      image: url,
    });

    // If user enters a URL, clear the selected file
    if (url) {
      setSelectedImage(null);
      setImagePreview(url);
    } else {
      setImagePreview(null);
    }
  };

  // Trigger file input click
  const handleSelectImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle module title change
  const handleModuleTitleChange = (index, value) => {
    const updatedModules = [...formData.modules];
    updatedModules[index].title = value;
    setFormData({
      ...formData,
      modules: updatedModules,
    });
  };

  // Handle topic change
  const handleTopicChange = (moduleIndex, topicIndex, value) => {
    const updatedModules = [...formData.modules];
    updatedModules[moduleIndex].topics[topicIndex] = value;
    setFormData({
      ...formData,
      modules: updatedModules,
    });
  };

  // Add new module
  const handleAddModule = () => {
    setFormData({
      ...formData,
      modules: [...formData.modules, { title: "", topics: [""] }],
    });
  };

  // Remove module
  const handleRemoveModule = (index) => {
    const updatedModules = [...formData.modules];
    updatedModules.splice(index, 1);
    setFormData({
      ...formData,
      modules: updatedModules.length
        ? updatedModules
        : [{ title: "", topics: [""] }],
    });
  };

  // Add new topic to a module
  const handleAddTopic = (moduleIndex) => {
    const updatedModules = [...formData.modules];
    updatedModules[moduleIndex].topics.push("");
    setFormData({
      ...formData,
      modules: updatedModules,
    });
  };

  // Remove topic from a module
  const handleRemoveTopic = (moduleIndex, topicIndex) => {
    const updatedModules = [...formData.modules];
    updatedModules[moduleIndex].topics.splice(topicIndex, 1);
    if (updatedModules[moduleIndex].topics.length === 0) {
      updatedModules[moduleIndex].topics.push(""); // Keep at least one topic
    }
    setFormData({
      ...formData,
      modules: updatedModules,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Validate modules
      const validModules = formData.modules.filter(
        (module) =>
          module.title.trim() !== "" &&
          module.topics.some((topic) => topic.trim() !== "")
      );

      // Create form data for file upload
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("description", formData.description);
      submitData.append("registration_date", formData.registration_date);
      submitData.append("duration", formData.duration);
      submitData.append("featured", formData.featured);

      // If we have modules, add them
      if (validModules.length > 0) {
        submitData.append("modules", JSON.stringify(validModules));
      }

      // Add either the file or the URL
      if (selectedImage) {
        submitData.append("courseImage", selectedImage);
      } else if (formData.image) {
        submitData.append("image", formData.image);
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/short-courses`,
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setShowAddModal(false);
      fetchCourses();
    } catch (err) {
      console.error("Error adding course:", err);
      setError("Failed to add course. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentCourse) return;

    try {
      setLoading(true);

      // Validate modules
      const validModules = formData.modules.filter(
        (module) =>
          module.title.trim() !== "" &&
          module.topics.some((topic) => topic.trim() !== "")
      );

      // Create form data for file upload
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("description", formData.description);
      submitData.append("registration_date", formData.registration_date);
      submitData.append("duration", formData.duration);
      submitData.append("featured", formData.featured);

      // If we have modules, add them
      if (validModules.length > 0) {
        submitData.append("modules", JSON.stringify(validModules));
      }

      // Add either the file or the URL
      if (selectedImage) {
        submitData.append("courseImage", selectedImage);
      } else if (formData.image) {
        submitData.append("image", formData.image);
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}/short-courses/${currentCourse.id}`,
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setShowEditModal(false);
      fetchCourses();
    } catch (err) {
      console.error(`Error updating course with ID ${currentCourse.id}:`, err);
      setError("Failed to update course. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentCourse) return;

    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/short-courses/${currentCourse.id}`
      );
      setShowDeleteModal(false);
      fetchCourses();
    } catch (err) {
      console.error(`Error deleting course with ID ${currentCourse.id}:`, err);
      setError("Failed to delete course. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Render module editor
  const renderModuleEditor = () => {
    return (
      <div className="mt-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Course Modules</h3>
          <button
            type="button"
            onClick={handleAddModule}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus size={16} className="mr-1" /> Add Module
          </button>
        </div>

        {formData.modules.map((module, moduleIndex) => (
          <div
            key={moduleIndex}
            className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Book className="w-5 h-5 text-blue-600 mr-2" />
                <h4 className="font-medium text-gray-800">
                  Module {moduleIndex + 1}
                </h4>
              </div>
              {formData.modules.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveModule(moduleIndex)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Module Title *
              </label>
              <input
                type="text"
                value={module.title}
                onChange={(e) =>
                  handleModuleTitleChange(moduleIndex, e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Introduction to Python"
              />
            </div>

            <div className="mb-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <List className="w-4 h-4 text-blue-600 mr-2" />
                  <label className="text-sm font-medium text-gray-700">
                    Topics
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => handleAddTopic(moduleIndex)}
                  className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                >
                  <Plus size={14} className="mr-1" /> Add Topic
                </button>
              </div>

              {module.topics.map((topic, topicIndex) => (
                <div key={topicIndex} className="flex items-center mb-2">
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) =>
                      handleTopicChange(moduleIndex, topicIndex, e.target.value)
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Variables and Data Types"
                  />
                  {module.topics.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveTopic(moduleIndex, topicIndex)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      <Minus size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render image upload section
  const renderImageUploadSection = () => {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Course Image
        </label>

        <div className="mt-1 mb-4">
          {imagePreview ? (
            <div className="relative">
              <img
                src={`${process.env.REACT_APP_API_URL}/${imagePreview}`}
                alt="Preview"
                className="h-40 w-full object-cover rounded-md border border-gray-300"
              />
              <button
                type="button"
                onClick={() => {
                  setImagePreview(null);
                  setSelectedImage(null);
                  setFormData({ ...formData, image: "" });
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="absolute top-2 right-2 bg-white/80 p-1 rounded-full hover:bg-white text-gray-700"
              >
                <XCircle size={20} />
              </button>
            </div>
          ) : (
            <div
              onClick={handleSelectImageClick}
              className="h-40 w-full border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
            >
              <Image className="w-10 h-10 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">Click to upload an image</p>
              <p className="text-xs text-gray-400 mt-1">
                JPG, PNG, GIF up to 5MB
              </p>
            </div>
          )}
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageChange}
          accept="image/*"
          className="hidden"
        />

        <div className="mt-1 flex items-center">
          <div className="flex-grow">
            <input
              type="text"
              name="image"
              value={formData.image}
              onChange={handleImageUrlChange}
              placeholder="Or enter an image URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="ml-2">
            <button
              type="button"
              onClick={handleSelectImageClick}
              className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-200 flex items-center"
            >
              <Upload size={16} className="mr-1" />
              Browse
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          Short Courses Management
        </h1>
        <p className="text-gray-600">
          Manage short courses that are displayed on the website. Add, edit, or
          remove courses as needed.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="relative w-full md:w-64">
          <input
            type="text"
            placeholder="Search courses..."
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        </div>

        <button
          onClick={handleAddClick}
          className="w-full md:w-auto flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusCircle size={18} />
          Add New Course
        </button>
      </div>

      {loading && courses.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-lg">No courses found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredCourses.map((course) => (
            <div
              key={course.id}
              className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/4 h-48 overflow-hidden">
                  {course.image ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}/${course.image}`}
                      alt={course.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400">No image</span>
                    </div>
                  )}
                </div>
                <div className="p-6 md:w-3/4">
                  <div className="flex flex-col md:flex-row justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-gray-900">
                          {course.name}
                        </h2>
                        {course.featured && (
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                            Featured
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mt-2 line-clamp-2">
                        {course.description}
                      </p>
                    </div>
                    <div className="flex mt-4 md:mt-0">
                      <button
                        onClick={() => handleEditClick(course)}
                        className="text-blue-600 hover:text-blue-800 p-2"
                        title="Edit course"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(course)}
                        className="text-red-600 hover:text-red-800 p-2"
                        title="Delete course"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-4">
                    {course.registration_date && (
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1 text-blue-600" />
                        {course.registration_date}
                      </div>
                    )}
                    {course.duration && (
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="w-4 h-4 mr-1 text-blue-600" />
                        {course.duration}
                      </div>
                    )}
                  </div>

                  {/* Module summary */}
                  {course.modules && course.modules.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="text-sm text-gray-500">
                        {course.modules.length} module
                        {course.modules.length !== 1 ? "s" : ""} •
                        {course.modules.reduce(
                          (total, module) => total + module.topics.length,
                          0
                        )}{" "}
                        topic
                        {course.modules.reduce(
                          (total, module) => total + module.topics.length,
                          0
                        ) !== 1
                          ? "s"
                          : ""}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Course Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Add New Course
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Course Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ></textarea>
                  </div>

                  {/* Image Upload Section */}
                  {renderImageUploadSection()}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Registration Date
                      </label>
                      <input
                        type="text"
                        name="registration_date"
                        value={formData.registration_date}
                        onChange={handleInputChange}
                        placeholder="e.g. January 15, 2025"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Duration
                      </label>
                      <input
                        type="text"
                        name="duration"
                        value={formData.duration}
                        onChange={handleInputChange}
                        placeholder="e.g. 5 days, 8 weeks"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="featured"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="featured"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Featured Course
                    </label>
                  </div>

                  {/* Module Editor */}
                  {renderModuleEditor()}
                </div>

                <div className="mt-8 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4" />
                        Adding...
                      </>
                    ) : (
                      "Add Course"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Course Modal */}
      {showEditModal && currentCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Edit Course
              </h2>
              <form onSubmit={handleUpdate}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Course Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ></textarea>
                  </div>

                  {/* Image Upload Section */}
                  {renderImageUploadSection()}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Registration Date
                      </label>
                      <input
                        type="text"
                        name="registration_date"
                        value={formData.registration_date}
                        onChange={handleInputChange}
                        placeholder="e.g. January 15, 2025"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Duration
                      </label>
                      <input
                        type="text"
                        name="duration"
                        value={formData.duration}
                        onChange={handleInputChange}
                        placeholder="e.g. 5 days, 8 weeks"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="featured-edit"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="featured-edit"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Featured Course
                    </label>
                  </div>

                  {/* Module Editor */}
                  {renderModuleEditor()}
                </div>

                <div className="mt-8 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4" />
                        Updating...
                      </>
                    ) : (
                      "Update Course"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && currentCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Confirm Deletion
              </h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete the course "{currentCourse.name}
                "? This action cannot be undone.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4" />
                      Deleting...
                    </>
                  ) : (
                    "Delete Course"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShortCourses;
